import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  EntityMenuMobile,
  MenuControlled,
} from "../../../common/components";
import { type MenuOption } from "../../../common/components/Menu/MenuControlled";
import { useSelector } from "../../../common/hooks/useSelector";
import DesignService from "../../../service/DesignService";
import DesignStateService from "../../../service/DesignStateService";
import { DesignState } from "../../../types";
import { useCart } from "../../../utils/CartHooks";
import { ROUTES } from "../../../constant/common.constant";
import { useDesignFetcher } from "../../../utils/DesignsFetcherHooks";
import { useThemeBreakpoints } from "../../../utils/themeUtils";
import DeleteModal from "../../DeleteModal/DeleteModal";
import MoveToProjectModal from "../../MoveToProjectModal/MoveToProjectModal";
import { EditModal } from "../../EditModal";
import CreateMockupModal from "./components/CreateMockupModal";
import { useAppLoading } from "../../../context/AppLoading/useAppLoading";
import { useAuth } from "../../../common/hooks/useAuth";
import { useTeam } from "../../../context/Team";
import LockDesignModal from "./components/LockDesignModal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  target: Element | null;
  design: DesignState;
  onUpdate: () => Promise<void>;
  setIsLoading?: (isLoading: boolean) => void;
  projectName: string;
}

const DesignCardMenu = ({
  isOpen,
  onClose,
  target,
  design: {
    id: designId,
    projectId,
    name: designName,
    lockedVersion,
    locked,
    images: designImages,
    updatedAt,
    family,
    product,
    productLine,
    design: designState,
    project,
  },
  projectName,
  onUpdate,
  setIsLoading,
}: Props) => {
  const { t } = useTranslation();
  const { teamId: teamIdFromContext } = useTeam();
  const teamId = teamIdFromContext || project?.teamId;
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsAppLoading } = useAppLoading();
  const { isMobile } = useThemeBreakpoints();
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [showCreateMockupModal, setShowCreateMockupModal] = useState(false);
  const [showLockDesignModal, setShowLockDesignModal] = useState(false);
  const { isAdmin } = useAuth();
  const {
    canDeleteDesign = true,
    canRenameDesign = true,
    canMoveDesign = true,
    canAddDesign = true,
  } = useSelector((state) => state.userInfo);

  const preventRedirectionOnEvent = location.pathname.includes(ROUTES.HOME);

  const { addToCart, fetchCartItemsAndUpdateStore } = useCart();

  const onRenameCloseClick = () => {
    setShowRenameModal(false);
  };

  const onDeleteClickClose = () => {
    setShowDeleteModal(false);
  };
  const onMoveClickClose = () => {
    setShowMoveProjectModal(false);
  };
  const onRename = useCallback(
    async (name: string) => {
      try {
        setIsLoading?.(true);
        await DesignService.renameDesign({ name, designId: designId });
        onUpdate();
      } catch (e) {
        console.error(e);
      } finally {
        setShowRenameModal(false);
        setIsLoading?.(false);
      }
    },
    [designId, setIsLoading, onUpdate]
  );

  const onDelete = useCallback(async () => {
    try {
      setIsLoading?.(true);
      await DesignService.deleteDesign(designId);
      await onUpdate();
      fetchCartItemsAndUpdateStore(); //Because deleting a design can impact the cart
    } catch (e) {
      console.error(e);
    } finally {
      setShowDeleteModal(false);
      setIsLoading?.(false);
      !preventRedirectionOnEvent &&
        navigate(generatePath(ROUTES.PROJECT, { projectId, teamId }));
    }
  }, [
    setIsLoading,
    designId,
    onUpdate,
    fetchCartItemsAndUpdateStore,
    preventRedirectionOnEvent,
    navigate,
    projectId,
    teamId,
  ]);

  const { fetchProductDefinition } = useDesignFetcher();

  const onAddToCart = async () => {
    //First get the Design State info of the design, then add to cart the design
    const {
      data: { design },
    } = await DesignStateService.getDesignState(designId);

    const {
      productDefinition: { sizes },
    } = await fetchProductDefinition(design.family, design.product);

    addToCart(designId, sizes, design.product.name);
  };

  const onDuplicate = async () => {
    onClose();
    setIsAppLoading(true);
    const res = await DesignStateService.duplicateDesignState({
      designId: designId,
    });

    const duplicatedDesignState = res.data;
    try {
      await DesignService.addDesign(
        {
          projectId,
          designId: duplicatedDesignState.designId,
          name: `${t("general.copyOf")} ${designName}`,
        },
        duplicatedDesignState.design.product
      );

      await onUpdate();
    } catch (e) {
      console.error(e);
    } finally {
      setIsAppLoading(false);
      !preventRedirectionOnEvent &&
        navigate(generatePath(ROUTES.PROJECT, { projectId, teamId }));
    }
  };

  const handleMenuItemClick = (callback: any) => async () => {
    await callback();
    onClose();
  };

  const options: MenuOption[] = [
    {
      labelKey: "general.rename",
      disabled: !canRenameDesign,
      onClick: handleMenuItemClick(() => setShowRenameModal(true)),
      hideIf: isMobile,
    },
    {
      labelKey: "design.delete_menu",
      disabled: !canDeleteDesign,
      onClick: handleMenuItemClick(() => setShowDeleteModal(true)),
    },
    {
      labelKey: "design.add_to_cart",
      onClick: handleMenuItemClick(onAddToCart),
    },
    {
      labelKey: "design.duplicate",
      disabled: !canAddDesign,
      onClick: handleMenuItemClick(onDuplicate),
    },
    {
      labelKey: "design.move_project",
      disabled: !canMoveDesign,
      onClick: handleMenuItemClick(() => setShowMoveProjectModal(true)),
    },
    {
      labelKey: "modal.create_mockup.title",
      onClick: handleMenuItemClick(() => setShowCreateMockupModal(true)),
    },
    {
      labelKey: locked ? "design.unlock" : "design.lock",
      hideIf: !isAdmin,
      adminOnly: true,
      onClick: handleMenuItemClick(async () => {
        if (locked) {
          await DesignService.lockDesign(designId, false, lockedVersion);
          onUpdate();
        } else {
          setShowLockDesignModal(true);
        }
      }),
    },
  ];

  return (
    <Box>
      {isMobile ? (
        <EntityMenuMobile
          isOpen={isOpen}
          onClose={onClose}
          options={options}
          name={designName || t("design.untitled_design")}
          onSave={onRename}
          updatedAt={updatedAt}
        />
      ) : (
        <MenuControlled
          options={options}
          isOpen={isOpen}
          onClose={onClose}
          target={target}
          data-test={`menu.${designName}`}
        />
      )}
      <EditModal
        open={showRenameModal}
        onSave={onRename}
        defaultValue={designName || t("design.untitled_design")}
        onClose={onRenameCloseClick}
        title={t("design.rename_title")}
        ctaButtonText={t("general.rename")}
      />
      <DeleteModal
        open={showDeleteModal}
        onDelete={onDelete}
        title={t("design.delete_modal_title")}
        message={
          locked
            ? t("design.delete_modal_message_locked")
            : t("design.delete_modal_message")
        }
        hideCta={locked}
        onClose={onDeleteClickClose}
      />
      <MoveToProjectModal
        open={showMoveProjectModal}
        onClose={onMoveClickClose}
        designId={designId}
        projectId={projectId}
      />
      {teamId && (
        <CreateMockupModal
          open={showCreateMockupModal}
          onClose={() => setShowCreateMockupModal(false)}
          teamId={teamId}
          designs={[
            {
              designId,
              designName,
              images: designImages || [],
              lockedVersion,
              family,
              product,
              productLine,
              decorations: designState?.decorations,
            },
          ]}
          projectName={projectName}
        />
      )}
      <LockDesignModal
        open={showLockDesignModal}
        onClose={() => setShowLockDesignModal(false)}
        designId={designId}
        oldVersion={lockedVersion}
        onUpdate={onUpdate}
      />
    </Box>
  );
};

export default memo(DesignCardMenu);
